/**
 * Factory function to create an AutocompleteProvider from a JSON schema.
 *
 * This allows consumers to define their autocomplete structure as pure JSON
 * that can be fetched from a server, then use this factory to create a
 * fully functional provider.
 */

import type { AutocompleteProvider, AutocompleteContext, AutocompleteResponse, ResolveRequest, ResolveResponse } from '../types';
import type { AutocompleteSchema } from '../types/schema';
import { SchemaResolver } from '../utils/schemaResolver';
import { buildTokenString } from '../utils/resolvedTokens';

/**
 * Options for the schema-based provider
 */
export interface SchemaProviderOptions {
  /**
   * Simulated network delay in milliseconds (for testing).
   * Set to 0 for production.
   */
  simulatedDelay?: number;

  /**
   * Enable debug logging to console
   */
  debug?: boolean;

  /**
   * Custom validation function for selected items.
   * Called in addition to schema-based validation.
   */
  onValidate?: (
    value: string,
    context: AutocompleteContext
  ) => Promise<{ valid: boolean; error?: string }>;
}

/**
 * Create an AutocompleteProvider from a JSON schema.
 *
 * @example
 * ```typescript
 * // Load schema from server
 * const schema = await fetch('/api/template-schema').then(r => r.json());
 *
 * // Create provider
 * const provider = createSchemaProvider(schema);
 *
 * // Use with RichTextEditor
 * <RichTextEditor autocompleteProvider={provider} />
 * ```
 */
export function createSchemaProvider(
  schema: AutocompleteSchema,
  options: SchemaProviderOptions = {}
): AutocompleteProvider {
  const { simulatedDelay = 0, debug = false, onValidate } = options;
  const resolver = new SchemaResolver(schema);

  const log = (...args: unknown[]) => {
    if (debug) {
      console.log('[SchemaProvider]', ...args);
    }
  };

  return {
    async onQuery(query: string, context: AutocompleteContext): Promise<AutocompleteResponse> {
      // Simulate network delay if configured
      if (simulatedDelay > 0) {
        await new Promise((resolve) => setTimeout(resolve, simulatedDelay));
      }

      const trimmedQuery = query.trim();
      log('Query:', trimmedQuery, 'Block context:', context.blockContext);

      // Resolve available options based on block context
      const resolved = resolver.resolve(trimmedQuery, context.blockContext);
      log('Resolved context:', resolved);

      // Handle block queries (starts with #)
      if (trimmedQuery.startsWith('#')) {
        return handleBlockQuery(trimmedQuery, resolved, resolver, log);
      }

      // Handle helper queries — first word is a known helper followed by a space
      const spaceIdx = trimmedQuery.indexOf(' ');
      if (spaceIdx > 0) {
        const firstWord = trimmedQuery.slice(0, spaceIdx);
        if (resolver.findHelperDefinition(firstWord)) {
          return handleHelperQuery(trimmedQuery, resolved, resolver, log);
        }
      }

      // Handle navigation queries (contains .)
      if (trimmedQuery.includes('.')) {
        const navResult = handleNavigationQuery(trimmedQuery, resolved, log);
        if (!navResult.error) {
          return navResult;
        }
        // Navigation failed — fall back to filtering root variables
        // (supports flat schemas where variable names contain dots)
        const baseContext = resolver.resolve('', context.blockContext);
        const fallbackItems = resolver.toAutocompleteItems(baseContext, trimmedQuery);
        if (fallbackItems.length > 0) {
          log('Navigation fallback items:', fallbackItems.length);
          return { items: fallbackItems };
        }
        return navResult;
      }

      // Standard query - return filtered items
      const items = resolver.toAutocompleteItems(resolved, trimmedQuery);
      log('Items:', items.length);

      // For helpers with required params: if the query exactly matches the
      // helper name, show argument hint instead of a selectable item.
      // The user needs to type a space to enter argument selection mode.
      const processed = items.map((item) => {
        if (
          item.metadata?.isHelper &&
          Array.isArray(item.metadata.parameters) &&
          (item.metadata.parameters as Array<{ required: boolean }>).some((p) => p.required) &&
          trimmedQuery.toLowerCase() === item.value.toLowerCase()
        ) {
          return {
            ...item,
            description: (item.description || item.label) + ' — type space to add argument',
            isError: true, // prevents selection in AutocompleteList
          };
        }
        return item;
      });

      return { items: processed };
    },

    async onResolve(requests: ResolveRequest[]): Promise<ResolveResponse[]> {
      if (simulatedDelay > 0) {
        await new Promise((resolve) => setTimeout(resolve, simulatedDelay));
      }

      return requests.map((req) => {
        // For block nodes, look up the block definition's description
        // and also resolve the variable/argument's description as token array
        if (req.isBlock && req.blockType) {
          const blockDef = resolver.findBlockDefinition(req.blockType);
          if (blockDef) {
            const variableDescription = buildTokenString(req.variable, req.blockContext, resolver);
            return {
              variable: req.variable,
              description: blockDef.description || null,
              variableDescription,
              isValid: true,
            };
          }
          return {
            variable: req.variable,
            description: null,
            isValid: false,
            error: `Unknown block type "${req.blockType}"`,
          };
        }

        // Handle helper tags (e.g., "currency Total" — first word is a helper name)
        if (req.variable.includes(' ')) {
          const firstWord = req.variable.split(/\s+/)[0];
          const helperDef = resolver.findHelperDefinition(firstWord);
          if (helperDef) {
            const tokenString = buildTokenString(req.variable, req.blockContext, resolver);
            return {
              variable: req.variable,
              description: tokenString || (helperDef.description || null),
              isValid: true,
            };
          }
        }

        const validation = resolver.validatePath(req.variable, req.blockContext);
        return {
          variable: req.variable,
          description: validation.definition?.description || null,
          isValid: validation.valid,
          error: validation.error,
        };
      });
    },

    async onSelect(item) {
      // Block types are always valid
      if (item.isBlockType) {
        return { valid: true };
      }

      // Run custom validation if provided
      if (onValidate) {
        return onValidate(item.value, {} as AutocompleteContext);
      }

      // All schema-defined items are valid
      return { valid: true };
    },
  };
}

/**
 * Handle queries that start with # (block creation)
 */
function handleBlockQuery(
  query: string,
  resolved: ReturnType<SchemaResolver['resolve']>,
  resolver: SchemaResolver,
  log: (...args: unknown[]) => void
): AutocompleteResponse {
  const parts = query.split(/\s+/);
  const blockKeyword = parts[0]; // e.g., "#if", "#each"
  const blockArg = parts.slice(1).join(' '); // e.g., "user.active"
  const blockType = blockKeyword.slice(1); // Remove #

  log('Block query:', { blockKeyword, blockArg, blockType });

  // Find matching block definition
  const matchingBlock = resolved.blocks.find(
    (b) => b.type.toLowerCase() === blockType.toLowerCase()
  );

  if (matchingBlock) {
    const def = matchingBlock.definition;

    // For blocks that don't need arguments, complete immediately
    if (def.argumentType === 'none') {
      log('Block ready (no arg needed):', blockType);
      return {
        items: [
          {
            id: `block-${blockType}`,
            label: `#${blockType}`,
            value: '',
            description: def.description,
            isBlockType: true,
            blockType: blockType,
          },
        ],
        isComplete: true,
      };
    }

    // For blocks that need arguments, check if we have one
    if (blockArg && def.argumentType === 'variable') {
      // Validate the argument
      const argItems = resolver.getValidBlockArguments(blockType, resolved);
      const isValidArg = argItems.some(
        (item) => item.value.toLowerCase() === blockArg.toLowerCase()
      );

      if (isValidArg) {
        const matchingArg = argItems.find(
          (item) => item.value.toLowerCase() === blockArg.toLowerCase()
        );
        log('Block ready with arg:', blockType, blockArg);
        return {
          items: [
            {
              id: `block-${blockType}-complete`,
              label: `#${blockType} ${blockArg}`,
              value: blockArg,
              description: def.description || `#${blockType}`,
              isBlockType: true,
              blockType: blockType,
              metadata: { variableDescription: matchingArg?.description },
            },
          ],
          isComplete: true,
        };
      }

      // Filter valid arguments by what's been typed
      const filteredArgs = argItems.filter((item) =>
        item.value.toLowerCase().includes(blockArg.toLowerCase())
      );

      if (filteredArgs.length > 0) {
        return {
          items: filteredArgs.map((item) => ({
            ...item,
            id: `block-${blockType}-arg-${item.value}`,
            label: `#${blockType} ${item.value}`,
            description: def.description || item.description,
            isBlockType: true,
            blockType: blockType,
          })),
        };
      }

      // No valid arguments match — return empty without error so freeform
      // mode can offer to insert the typed value as-is
      return { items: [] };
    }

    // Show valid arguments for this block
    if (def.argumentType === 'variable') {
      const argItems = resolver.getValidBlockArguments(blockType, resolved);
      return {
        items: argItems.map((item) => ({
          ...item,
          id: `block-${blockType}-arg-${item.value}`,
          label: `#${blockType} ${item.value}`,
          description: item.description,
          isBlockType: true,
          blockType: blockType,
        })),
      };
    }
  }

  // Show matching block types while typing keyword
  const items = resolver.toAutocompleteItems(resolved, query);
  const blockItems = items.filter((item) => item.isBlockType);

  return { items: blockItems };
}

/**
 * Handle queries where the first word is a known helper (e.g., "currency Total")
 */
function handleHelperQuery(
  query: string,
  resolved: ReturnType<SchemaResolver['resolve']>,
  resolver: SchemaResolver,
  log: (...args: unknown[]) => void
): AutocompleteResponse {
  const parts = query.split(/\s+/);
  const helperName = parts[0];
  const argString = parts.slice(1).join(' ');

  log('Helper query:', { helperName, argString });

  const helper = resolver.findHelperDefinition(helperName);
  if (!helper) {
    return { items: [], error: `Unknown helper "${helperName}"` };
  }

  // For now, helpers take a single argument (first required parameter).
  // This can be extended for multi-parameter helpers in the future.
  const paramIndex = 0;

  if (argString) {
    // User has typed (or is typing) an argument — validate or filter
    const argItems = resolver.getValidHelperArguments(helperName, paramIndex, resolved);
    const isValidArg = argItems.some(
      (item) => item.value.toLowerCase() === argString.toLowerCase()
    );

    if (isValidArg) {
      log('Helper ready with arg:', helperName, argString);
      return {
        items: [
          {
            id: `helper-${helperName}-complete`,
            label: `${helperName} ${argString}`,
            value: `${helperName} ${argString}`,
            description: helper.description,
          },
        ],
        isComplete: true,
      };
    }

    // Filter valid arguments by what's been typed
    const filteredArgs = argItems.filter((item) =>
      item.value.toLowerCase().includes(argString.toLowerCase())
    );

    if (filteredArgs.length > 0) {
      return {
        items: filteredArgs.map((item) => ({
          ...item,
          id: `helper-${helperName}-arg-${item.value}`,
          label: `${helperName} ${item.value}`,
          value: `${helperName} ${item.value}`,
          description: item.description || helper.description,
        })),
      };
    }

    // No valid arguments match — return empty without error so freeform
    // mode can offer to insert the typed value as-is
    return { items: [] };
  }

  // No argument yet — show valid variables for this parameter
  const argItems = resolver.getValidHelperArguments(helperName, paramIndex, resolved);
  return {
    items: argItems.map((item) => ({
      ...item,
      id: `helper-${helperName}-arg-${item.value}`,
      label: `${helperName} ${item.value}`,
      value: `${helperName} ${item.value}`,
      description: item.description || helper.description,
    })),
  };
}

/**
 * Handle queries with dot navigation
 */
function handleNavigationQuery(
  query: string,
  resolved: ReturnType<SchemaResolver['resolve']>,
  log: (...args: unknown[]) => void
): AutocompleteResponse {
  const segments = query.split('.');
  const lastSegment = segments[segments.length - 1];

  log('Navigation query:', { segments, lastSegment, pathPrefix: resolved.pathPrefix });

  // Get items for this navigation level
  const items = resolved.variables
    .filter((v) => {
      // Filter by the last segment if not empty
      if (lastSegment) {
        const varName = v.path.split('.').pop() || v.path;
        return varName.toLowerCase().includes(lastSegment.toLowerCase());
      }
      return true;
    })
    .map((v) => ({
      id: `var-${v.path}`,
      label: v.path,
      value: v.path,
      description: v.definition.description,
      metadata: {
        type: v.definition.type,
        isWildcard: v.isWildcard,
      },
    }));

  // If we're navigating into nothing valid, show error
  if (items.length === 0 && resolved.variables.length === 0) {
    const parentPath = segments.slice(0, -1).join('.');
    return {
      items: [],
      error: `"${parentPath}" has no accessible properties`,
    };
  }

  return { items };
}

/**
 * Parse a JSON schema string and create a provider.
 * Throws if the JSON is invalid.
 */
export function createSchemaProviderFromJson(
  schemaJson: string,
  options?: SchemaProviderOptions
): AutocompleteProvider {
  const schema = JSON.parse(schemaJson) as AutocompleteSchema;
  return createSchemaProvider(schema, options);
}
